import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Home,
  Users,
  BookOpen,
  MessageSquare,
  User,
  LogOut,
  Menu,
  X,
  Zap,
  Bell,
  Settings,
  Newspaper
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_player-connect-ikons/artifacts/bp71r7p3_IMG_9412.png';

function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Navigation items based on role
  const getNavItems = () => {
    const baseItems = [
      { path: '/dashboard', icon: Home, label: 'Inicio' },
    ];

    if (user?.role === 'player') {
      return [
        ...baseItems,
        { path: '/ikons', icon: Zap, label: 'Ikons' },
        { path: '/resources', icon: BookOpen, label: 'Recursos' },
        { path: '/messages', icon: MessageSquare, label: 'Mensajes' },
        { path: '/profile', icon: User, label: 'Perfil' },
      ];
    } else {
      // Agent navigation
      return [
        ...baseItems,
        { path: '/ikons/admin', icon: Users, label: 'Jugadores' },
        { path: '/resources', icon: BookOpen, label: 'Recursos' },
        { path: '/user-management', icon: Settings, label: 'Usuarios' },
        { path: '/profile', icon: User, label: 'Perfil' },
      ];
    }
  };

  const navItems = getNavItems();

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Mobile Top Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100">
        <div className="flex items-center justify-between px-4 h-14">
          <img src={LOGO_URL} alt="Ikons" className="h-8" />
          <div className="flex items-center gap-2">
            <button className="p-2 text-[#808080] hover:text-[#4F4F4F]">
              <Bell className="w-5 h-5" />
            </button>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-[#808080] hover:text-[#4F4F4F]"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Dropdown Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed top-14 left-0 right-0 z-40 bg-white border-b border-gray-100 shadow-lg">
          <div className="p-4 space-y-1">
            {user?.role === 'agent' && (
              <>
                <Link
                  to="/news"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#4F4F4F] hover:bg-gray-50"
                >
                  <Newspaper className="w-5 h-5" />
                  <span>Noticias</span>
                </Link>
                <Link
                  to="/admin-setup"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#4F4F4F] hover:bg-gray-50"
                >
                  <Settings className="w-5 h-5" />
                  <span>Admin Setup</span>
                </Link>
              </>
            )}
            <button
              onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-red-500 hover:bg-red-50 w-full"
            >
              <LogOut className="w-5 h-5" />
              <span>Cerrar Sesión</span>
            </button>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-gray-100 flex-col z-50">
        {/* Logo */}
        <div className="p-6 border-b border-gray-100">
          <img src={LOGO_URL} alt="Ikons" className="h-10" />
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A67C00] flex items-center justify-center text-white font-bold">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[#4F4F4F] truncate">{user?.name}</p>
              <p className="text-xs text-[#808080] capitalize">{user?.role === 'agent' ? 'Agente' : 'Jugador'}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  active 
                    ? 'bg-[#D4AF37]/10 text-[#D4AF37] font-semibold' 
                    : 'text-[#808080] hover:bg-gray-50 hover:text-[#4F4F4F]'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            );
          })}
          
          {user?.role === 'agent' && (
            <>
              <div className="pt-4 mt-4 border-t border-gray-100">
                <p className="px-4 text-xs text-[#808080] uppercase tracking-wider mb-2">Admin</p>
              </div>
              <Link
                to="/news"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive('/news') 
                    ? 'bg-[#D4AF37]/10 text-[#D4AF37] font-semibold' 
                    : 'text-[#808080] hover:bg-gray-50 hover:text-[#4F4F4F]'
                }`}
              >
                <Newspaper className="w-5 h-5" />
                <span>Noticias</span>
              </Link>
              <Link
                to="/admin-setup"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isActive('/admin-setup') 
                    ? 'bg-[#D4AF37]/10 text-[#D4AF37] font-semibold' 
                    : 'text-[#808080] hover:bg-gray-50 hover:text-[#4F4F4F]'
                }`}
              >
                <Settings className="w-5 h-5" />
                <span>Admin Setup</span>
              </Link>
            </>
          )}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-gray-100">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-red-500 hover:text-red-600 hover:bg-red-50"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-100 safe-area-bottom">
        <div className="flex items-center justify-around h-16">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`bottom-nav-${item.label.toLowerCase()}`}
                className={`flex flex-col items-center justify-center w-full h-full transition-colors ${
                  active ? 'text-[#D4AF37]' : 'text-[#808080]'
                }`}
              >
                <Icon className="w-5 h-5 mb-1" />
                <span className="text-xs font-medium">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Main Content */}
      <main className="lg:ml-64 pt-14 lg:pt-0 pb-20 lg:pb-0 min-h-screen">
        {children}
      </main>
    </div>
  );
}

export default Layout;
